import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import arrowLeftIcon from 'assets/images/icons/arrow-left.svg';
import eyeOffIcon from 'assets/images/icons/eye-off.svg';
import lockGreyIcon from 'assets/images/icons/lock-grey.svg';
import arrowRightIcon from 'assets/images/icons/arrow-right-black.svg';
import AlertModal from 'components/common-components/AlertModal';
import Error from 'components/Error';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import SelectionComponent from 'components/common-components/SelectionComponentNew';
import TextField from 'components/form-components/TextField';
import ToggleSwitch from 'components/form-components/ToggleSwitch';
import { addFolderSchema } from './utils';
import { CallAPI } from 'actions/General';

function AddFolder() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [apiError, setApiError] = useState('');
  const [allGroups, setAllGroups] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [values, setValues] = useState({
    folderName: '',
    designer: [],
    supervisor: [],
    folderId: '',
    hide: false,
    lock: false,
  });
  const [formList, setFormList] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
  };

  const submit = async () => {
    setLoadingType('save');
    setTouched({});
    try {
      await addFolderSchema.validate({ ...values }, { abortEarly: false });
      let APICallName = 'ADD_FOLDER';
      let folderId = null;
      let designerGroupList = [];
      let supervisorGroupList = [];
      values.designer.map((item) => designerGroupList.push(item.id));
      values.supervisor.map((item) => supervisorGroupList.push(item.id));
      let APIData = {
        name: values.folderName,
        designer: { users: [], groups: designerGroupList },
        supervisor: { users: [], groups: supervisorGroupList },
        isLocked: values.lock,
        isHidden: values.hide,
      };
      if (location?.state?.type === 'edit') {
        APICallName = 'UPDATE_FOLDER';
        folderId = values.folderId;
      }
      let result = await CallAPI(APICallName, APIData, folderId, setLoading, setApiError, null);
      if (result.status) {
        navigate('/forms');
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
    setLoading(false);
  };

  const close = async () => {
    navigate(-1);
  };

  const getFolderDetails = async (folderId) => {
    let result = await CallAPI('GET_FOLDER_DETAILS', null, folderId, setLoading, setApiError, null);
    if (result.status) {
      let selectedDesignerGroups = [];
      let selectedSupervisorGroups = [];
      result.data.designer.groups.map((singleSelectedGroup) =>
        selectedDesignerGroups.push({
          id: singleSelectedGroup._id,
          name: singleSelectedGroup.groupName,
        }),
      );
      result.data.supervisor.groups.map((singleSelectedGroup) =>
        selectedSupervisorGroups.push({
          id: singleSelectedGroup._id,
          name: singleSelectedGroup.groupName,
        }),
      );
      setValues({
        folderName: result.data.name,
        designer: selectedDesignerGroups,
        supervisor: selectedSupervisorGroups,
        folderId: result.data._id,
        hide: result.data.isHidden,
        lock: result.data.isLocked,
      });
      setFormList(result.data.forms);
    }
  };

  useEffect(() => {
    if (location && location.state && location.state.type === 'edit') {
      getFolderDetails(location.state.folderId);
    }
  }, [location]);

  const deleteFolder = async () => {
    setLoadingType('delete');
    let result = await CallAPI(
      'DELETE_FOLDER',
      {
        folders: [{ id: values.folderId, title: values.folderName }],
      },
      null,
      setLoading,
      setApiError,
    );
    if (result.status) {
      navigate(-1);
    }
  };

  const getAllGroups = async () => {
    const result = await CallAPI('GET_ALL_GROUPS', {}, null, null, null, null);
    if (result.status) {
      const groups = result.data.map((record) => ({
        id: record._id,
        name: record.groupName,
      }));
      setAllGroups(groups);
    }
  };

  useEffect(() => {
    getAllGroups();
  }, []);

  return (
    <div className='add-group theme-background-white-1 p-4'>
      <div className='d-flex align-items-center cursor-pointer' onClick={close}>
        <img src={arrowLeftIcon} className='back-arrow me-2' alt='Loading...' />
        <label>{t('text_back')}</label>
      </div>
      {apiError ? <Error msg={apiError} /> : <></>}
      <div className='m-4 d-flex flex-column gap-4'>
        <div className='d-flex flex-column gap-2'>
          {location.state?.type !== 'edit' && (
            <span className='theme-size-1_5 theme-font-inter-medium theme-text-black-1'>
              {t('text_add_new_folder')}
            </span>
          )}
          <span className='m-0 lh-1'>{t('text_form_folder_access_description')}</span>
        </div>
        <div>
          <label className='mb-2'>{t('field_folder_name')}</label>
          <div className='d-flex align-items-center gap-2'>
            <TextField
              name='folderName'
              placeholder='field_folder_name'
              value={values.folderName}
              handleChange={handleChange}
              error={errors.folderName}
              touched={touched.folderName}
              shrinkFieldOnly
            />
            <div className='d-flex gap-5 mx-4'>
              <div className='d-flex align-items-center gap-3'>
                <div className='d-flex align-items-center gap-2'>
                  <img src={lockGreyIcon} className='svg-black' />
                  <label className='theme-text-black-1'>{t('text_lock')}</label>
                </div>
                <ToggleSwitch name='lock' value={values.lock} handleChange={handleChange} />
              </div>
              <div className='d-flex align-items-center gap-3'>
                <div className='d-flex align-items-center gap-2'>
                  <img src={eyeOffIcon} className='svg-black' />
                  <label className='theme-text-black-1'>{t('text_hide')}</label>
                </div>
                <ToggleSwitch name='hide' value={values.hide} handleChange={handleChange} />
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className='mb-2'>{t('text_folder_forms')}</label>
          <div className='accordion'>
            <div className='accordion-item theme-text-black-1 theme-size-1_1 theme-font-inter-medium theme-background-white-2 py-3 px-3 mb-0'>
              {t('text_name')}
            </div>

            <div className='accordion-item p-3 theme-text-grey-1'>
              {formList.length > 0 ? (
                <div className='d-flex flex-column gap-3'>
                  {formList.map((form, index) => (
                    <label key={index}>{form.title}</label>
                  ))}
                </div>
              ) : (
                <label className='w-100 text-center'>{t('text_empty')}</label>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className='mb-2'>{t('text_folder_access')}</div>
          {!selectedRow && (
            <div className='accordion'>
              <div
                className='accordion-item p-3 d-flex'
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedRow('designer')}
              >
                <span className='w-25'>{t('text_designer')}</span>
                <div className='w-100 d-flex justify-content-between'>
                  <span className='theme-background-green-4 theme-text-green-1 rounded-2 px-2 py-1 theme-font-inter-light'>
                    {values.designer.length} {t('text_groups')}
                  </span>
                  <img src={arrowRightIcon} />
                </div>
              </div>

              <div
                className='accordion-item p-3 d-flex'
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedRow('supervisor')}
              >
                <span className='w-25'>{t('text_view_submission')}</span>
                <div className='w-100 d-flex justify-content-between'>
                  <span className='theme-background-green-4 theme-text-green-1 rounded-2 px-2 py-1 theme-font-inter-light'>
                    {values.supervisor.length} {t('text_groups')}
                  </span>
                  <img src={arrowRightIcon} />
                </div>
              </div>
            </div>
          )}

          {selectedRow && (
            <>
              <div
                className='d-flex align-items-center cursor-pointer mb-3'
                onClick={() => setSelectedRow(null)}
              >
                <img src={arrowLeftIcon} className='back-arrow me-2' alt='Loading...' />
                <label>{t('text_back')}</label>
              </div>

              <SelectionComponent
                title={selectedRow === 'designer' ? 'text_designer' : 'text_view_submission'}
                error={errors[selectedRow]}
                list={allGroups}
                selectedList={values[selectedRow]}
                setSelectedList={(value) => handleChange(selectedRow, value)}
              />
            </>
          )}
        </div>

        {!selectedRow && (
          <div className={`d-flex justify-content-end gap-3 mt-5`}>
            <FormLoadingButton
              loading={false}
              text='button_cancel'
              variant='white-1'
              onClick={close}
            />
            {values.folderId !== '' && (
              <FormLoadingButton
                loading={loading && loadingType === 'delete'}
                text='button_delete'
                variant='red-1'
                onClick={() => setShowDeleteConfirm(true)}
              />
            )}
            <FormLoadingButton
              text='button_save'
              variant='green-1'
              onClick={submit}
              loading={loading && loadingType === 'save'}
            />
          </div>
        )}

        <AlertModal
          show={showDeleteConfirm}
          showCloseButton={true}
          closeModal={() => setShowDeleteConfirm(false)}
          variant='delete'
          message={'alert_delete_folder'}
          actionButtons={[
            { text: t('button_confirm'), variant: 'red-1', onClick: () => deleteFolder() },
            {
              text: t('button_cancel'),
              variant: 'white-1',
              onClick: () => setShowDeleteConfirm(false),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default AddFolder;
